/**
 * Seção "Classificação" das Configurações: os quatro catálogos por unidade
 * (demandas, não conformidades, processos de origem, causas-raiz).
 * Tabela com layout fixo (zero scroll lateral); criar/editar em painel.
 */
import ConfirmDialog from "../../../../components/ConfirmDialog";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../../components/AsyncButton";
import { useAuth } from "../../../../context/AuthContext";
import {
  IMPUTACAO_LABEL,
  excluirItemCatalogo,
  listarCatalogo,
  obterCatalogosSelect,
} from "../../../../services/solicitacoes";
import { TableWrapper } from "../../../../styles/shared";
import { Carregando, Vazio } from "../../_shared/Estados";
import { unwrapLista } from "../helpers";
import {
  Checkbox, ErrorMsg, GhostBtn, Pill, PrimaryBtn, Table, Td, Th, ToggleBtn, Tr, ViewToggle,
} from "../styles";
import CatalogoItemPanel from "./CatalogoItemPanel";
import { CATALOGOS, ORDEM_CATALOGOS } from "./regras";

function detalheDoItem(tipo, item, listas) {
  const nomeDe = (lista, codigo) => (lista || []).find((o) => o.codigo === codigo)?.nome || codigo;
  if (tipo === "ncs") return item.familia_display || nomeDe(listas?.familias, item.familia) || "—";
  if (tipo === "causas") return item.categoria_6m_display || "—";
  if (tipo === "processos") {
    return [nomeDe(listas?.niveis, item.nivel), item.imputacao_padrao && IMPUTACAO_LABEL[item.imputacao_padrao]]
      .filter(Boolean).join(" · ") || "—";
  }
  return [
    item.imputacao_padrao && IMPUTACAO_LABEL[item.imputacao_padrao],
    item.entra_no_custo_nq ? "entra no custo NQ" : "fora do custo NQ",
  ].filter(Boolean).join(" · ");
}

export default function CatalogosSecao({ podeEditar }) {
  const { request } = useAuth();
  const [tipo, setTipo] = useState("demandas");
  const [aExcluir, setAExcluir] = useState(null);
  const [itens, setItens] = useState(null);
  const [listas, setListas] = useState(null);
  const [inativos, setInativos] = useState(false);
  const [erro, setErro] = useState("");
  const [editando, setEditando] = useState(null); // item | "novo"

  const carregar = useCallback(async () => {
    setErro("");
    try {
      const r = await listarCatalogo(request, tipo, { inativos });
      setItens(unwrapLista(r));
    } catch (e) {
      setErro(e?.message || "Erro ao carregar o catálogo.");
      setItens([]);
    }
  }, [request, tipo, inativos]);

  useEffect(() => { setItens(null); carregar(); }, [carregar]);

  // Listas fixas (famílias, níveis, 6M, imputações) + processos ativos.
  const carregarListas = useCallback(() => {
    obterCatalogosSelect(request).then(setListas).catch(() => setListas({}));
  }, [request]);
  useEffect(() => { carregarListas(); }, [carregarListas]);

  const ordenados = useMemo(
    () => [...(itens || [])].sort((a, b) => (a.ordem - b.ordem) || a.nome.localeCompare(b.nome)),
    [itens],
  );

  const excluir = (item) => async () => setAExcluir(item);
  const excluirDeVez = async () => {
    await excluirItemCatalogo(request, tipo, aExcluir.id);
    setAExcluir(null);
    await carregar();
  };

  const meta = CATALOGOS[tipo];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <ViewToggle>
          {ORDEM_CATALOGOS.map((t) => (
            <ToggleBtn key={t} type="button" $active={tipo === t} onClick={() => setTipo(t)}>
              {CATALOGOS[t].titulo}
            </ToggleBtn>
          ))}
        </ViewToggle>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink-2)" }}>
          <Checkbox checked={inativos} onChange={(e) => setInativos(e.target.checked)} />
          Mostrar inativos
        </label>
        {podeEditar && (
          <PrimaryBtn type="button" style={{ marginLeft: "auto" }} onClick={() => setEditando("novo")}>
            <FiPlus size={14} /> Nova {meta.singular}
          </PrimaryBtn>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>{meta.hint}</div>

      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      {itens === null ? <Carregando /> : ordenados.length === 0 ? (
        <Vazio titulo="Nenhum item neste catálogo" />
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: 64 }} />
              <col />
              <col style={{ width: "22%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: 76 }} />
              <col style={{ width: 92 }} />
              {podeEditar && <col style={{ width: 96 }} />}
            </colgroup>
            <thead>
              <tr>
                <Th>Ordem</Th><Th>Nome</Th><Th>Código</Th><Th>Detalhe</Th>
                <Th title="Itens de solicitação classificados com este valor">Em uso</Th>
                <Th>Situação</Th>
                {podeEditar && <Th>Ações</Th>}
              </tr>
            </thead>
            <tbody>
              {ordenados.map((item) => {
                const detalhe = detalheDoItem(tipo, item, listas);
                return (
                  <Tr key={item.id} onClick={podeEditar ? () => setEditando(item) : undefined}
                      style={podeEditar ? undefined : { cursor: "default" }}>
                    <Td className="mono">{item.ordem}</Td>
                    <Td title={item.nome}>
                      {item.nome}
                      {item.substituido_por_nome ? ` (substituído por ${item.substituido_por_nome})` : ""}
                    </Td>
                    <Td className="mono" title={item.codigo}>{item.codigo}</Td>
                    <Td title={detalhe}>{detalhe}</Td>
                    <Td className="mono">{item.em_uso ?? 0}</Td>
                    <Td>
                      {item.sistema
                        ? <Pill $tone="neutral" title="Item de sistema">sistema</Pill>
                        : <Pill $tone={item.ativo ? "success" : "neutral"}>{item.ativo ? "ativo" : "inativo"}</Pill>}
                    </Td>
                    {podeEditar && (
                      <Td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <GhostBtn type="button" title="Editar" aria-label={`Editar ${item.nome}`}
                                    style={{ height: 28, padding: "0 8px" }} onClick={() => setEditando(item)}>
                            <FiEdit2 size={13} />
                          </GhostBtn>
                          {!item.sistema && (item.em_uso ?? 0) === 0 && (
                            <AsyncButton variant="icon" silentError successMessage="Item excluído."
                                         title="Excluir (nunca foi usado)" aria-label={`Excluir ${item.nome}`}
                                         onClick={excluir(item)}>
                              <FiTrash2 size={13} />
                            </AsyncButton>
                          )}
                        </div>
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      {editando && (
        <CatalogoItemPanel
          tipo={tipo}
          item={editando === "novo" ? null : editando}
          itens={itens || []}
          listas={listas}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); carregar(); if (tipo === "processos") carregarListas(); }}
        />
      )}
      <ConfirmDialog
        open={Boolean(aExcluir)}
        titulo={aExcluir ? `Excluir "${aExcluir.nome}"?` : ""}
        descricao="Só é possível excluir o que nunca foi usado; item usado deve ser desativado."
        confirmarLabel="Excluir"
        tone="danger"
        onConfirmar={excluirDeVez}
        onCancelar={() => setAExcluir(null)}
      />
    </div>
  );
}
